import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../lib/api';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Briefcase, GraduationCap, MapPin, Loader2 } from 'lucide-react';

/**
 * ProfilePreview — wraps a trigger element (avatar, name, etc.) with:
 *   • Hover: shows a mini profile card
 *   • Click: navigates to full profile page
 */
export default function ProfilePreview({ userId, userName, userPhoto, children }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchProfile = async () => {
    if (fetched || loading || !userId) return;
    setLoading(true);
    try {
      const res = await usersAPI.getProfile(userId);
      setProfile(res.data);
    } catch (err) {
      console.error('Failed to fetch profile preview:', err);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  const goToProfile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/profile/${userId}`);
  };

  return (
    <HoverCard openDelay={300} closeDelay={150} onOpenChange={(open) => { if (open) fetchProfile(); }}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={goToProfile}
          className="cursor-pointer text-left appearance-none bg-transparent border-none p-0 m-0"
        >
          {children}
        </button>
      </HoverCardTrigger>

      <HoverCardContent
        className="w-72 rounded-2xl border border-slate-100 bg-white shadow-lg p-0 overflow-hidden"
        side="bottom"
        align="start"
        sideOffset={8}
      >
        {loading && !profile ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-gray" />
          </div>
        ) : profile ? (
          <div>
            {/* Cover / gradient header */}
            <div className="h-16 bg-gradient-to-br from-dark-slate to-slate-700 relative">
              {profile.cover_photo && (
                <img src={profile.cover_photo} alt="" className="w-full h-full object-cover" />
              )}
            </div>

            {/* Avatar overlapping the header */}
            <div className="px-4 -mt-7">
              <Avatar className="w-14 h-14 border-2 border-white shadow-sm">
                <AvatarImage src={profile.profile_photo || userPhoto} />
                <AvatarFallback className="bg-mint-fresh text-dark-slate text-lg font-semibold">
                  {(profile.name || userName)?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Info */}
            <div className="px-4 pt-2 pb-4">
              <p className="font-semibold text-dark-slate text-sm leading-tight">
                {profile.name || userName}
              </p>

              {(profile.title || profile.company) && (
                <p className="text-xs text-slate-gray mt-0.5 flex items-center gap-1">
                  <Briefcase className="w-3 h-3 flex-shrink-0" />
                  {[profile.title, profile.company].filter(Boolean).join(' at ')}
                </p>
              )}

              {profile.institution && (
                <p className="text-xs text-slate-gray mt-0.5 flex items-center gap-1">
                  <GraduationCap className="w-3 h-3 flex-shrink-0" />
                  {profile.institution}
                  {profile.graduation_year ? ` '${String(profile.graduation_year).slice(-2)}` : ''}
                </p>
              )}

              {profile.current_location && (
                <p className="text-xs text-slate-gray mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  {profile.current_location}
                </p>
              )}

              {profile.bio && (
                <p className="text-xs text-slate-500 mt-2 line-clamp-2">{profile.bio}</p>
              )}

              {/* Interests tags */}
              {profile.interests?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {profile.interests.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0 bg-mint-fresh/20 text-dark-slate border-0">
                      {tag}
                    </Badge>
                  ))}
                  {profile.interests.length > 3 && (
                    <span className="text-[10px] text-slate-gray">+{profile.interests.length - 3}</span>
                  )}
                </div>
              )}

              <Button
                size="sm"
                onClick={goToProfile}
                className="w-full mt-3 rounded-full bg-dark-slate hover:bg-dark-slate/90 text-white text-xs h-8"
              >
                View Full Profile
              </Button>
            </div>
          </div>
        ) : (
          /* Fallback if fetch failed */
          <div className="p-4 text-center">
            <Avatar className="w-12 h-12 mx-auto mb-2">
              <AvatarImage src={userPhoto} />
              <AvatarFallback className="bg-mint-fresh text-dark-slate">{userName?.charAt(0)}</AvatarFallback>
            </Avatar>
            <p className="font-semibold text-dark-slate text-sm">{userName}</p>
            <Button
              size="sm"
              onClick={goToProfile}
              className="mt-2 rounded-full bg-dark-slate hover:bg-dark-slate/90 text-white text-xs h-8"
            >
              View Profile
            </Button>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
